"use client"

import { useState, useEffect, useCallback } from "react"
import { useAuth } from "@/hooks/auth/use-auth"
import type { ComposedRoleBundle } from "@/lib/ai/roles/compose"
import type {
  MessageAgentStatus,
  MessageAgentConfig,
  ChannelType,
  ChannelStatus,
} from "@/lib/agents/messaging/types"
import { useTranslations } from "next-intl"


export interface MessageAgentChannel {
  id: string
  message_agent_id: string
  channel_type: ChannelType
  account_name: string | null
  external_account_id: string | null
  status: ChannelStatus
  webhook_verified: boolean
  last_error?: string | null
  connected_at: string | null
  /** Display order: channels keep the order they were connected in. */
  created_at?: string
  /** Gates AI replies and follow-ups only — transport and operator sends stay live. */
  ai_replies_enabled: boolean
  config?: Record<string, unknown>
}

export interface MessageAgentWithChannels {
  id: string
  tenant_id: string
  created_by: string
  name: string
  status: MessageAgentStatus
  config: MessageAgentConfig
  channels: MessageAgentChannel[]
  created_at: string
  updated_at: string
}

export interface CreateMessageAgentData {
  name: string
  /** Agent role slugs (ai_use_case_roles); defaults to ['custom'] server-side. */
  roles?: string[]
  /** Prompts from the preview, so apply reuses that ONE composition instead of generating again. */
  composed?: ComposedRoleBundle
  /** Display name; only meaningful for the custom-only role set. */
  role_label?: string | null
}

export interface UpdateMessageAgentData {
  name?: string
  status?: MessageAgentStatus
  config?: Partial<MessageAgentConfig>
  /** Role change; pairs with apply_preset. */
  roles?: string[]
  /** Prompts from the preview; ignored unless apply_preset is on. */
  composed?: ComposedRoleBundle
  /** false = metadata-only change (default true — apply, overwriting the prompt). */
  apply_preset?: boolean
  /** Display name; cleared server-side on a named preset. */
  role_label?: string | null
}

export function useMessagingAgents() {
  const tUi = useTranslations("Feedback.UseAgents")

  const [agents, setAgents] = useState<MessageAgentWithChannels[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { fetchWithAuth, isAuthenticated, isLoading: isAuthLoading } = useAuth()

  const loadAgents = useCallback(async () => {
    if (isAuthLoading) return
    if (!isAuthenticated) {
      setIsLoading(false)
      return
    }

    setError(null)

    try {
      const response = await fetchWithAuth("/api/agents/messaging")

      if (response.ok) {
        const data = await response.json()
        setAgents(data.agents || [])
      } else {
        const errorData = await response.json()
        setError(errorData.error || "Failed to load agents")
      }
    } catch (err) {
      console.error("Error loading messaging agents:", err)
      setError(tUi("failedToLoadAgents"))
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, isAuthenticated, isAuthLoading])

  useEffect(() => {
    loadAgents()
  }, [loadAgents])

  const getAgent = useCallback(
    (id: string) => agents.find((agent) => agent.id === id),
    [agents]
  )

  const createAgent = useCallback(
    async (agentData: CreateMessageAgentData): Promise<MessageAgentWithChannels> => {
      const response = await fetchWithAuth("/api/agents/messaging", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(agentData),
      })

      if (response.ok) {
        const data = await response.json()
        const newAgent = data.agent
        setAgents((prev) => [newAgent, ...prev])
        return newAgent
      } else {
        const errorData = await response.json()
        throw new Error(errorData.error || "Failed to create agent")
      }
    },
    [fetchWithAuth]
  )

  const updateAgent = useCallback(
    async (id: string, agentData: UpdateMessageAgentData): Promise<MessageAgentWithChannels> => {
      const response = await fetchWithAuth(`/api/agents/messaging/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(agentData),
      })

      if (response.ok) {
        const data = await response.json()
        const updatedAgent = data.agent
        setAgents((prev) => prev.map((a) => (a.id === id ? updatedAgent : a)))
        return updatedAgent
      } else {
        const errorData = await response.json()
        throw new Error(errorData.error || "Failed to update agent")
      }
    },
    [fetchWithAuth]
  )

  const deleteAgent = useCallback(
    async (id: string): Promise<void> => {
      const response = await fetchWithAuth(`/api/agents/messaging/${id}`, {
        method: "DELETE",
      })

      if (response.ok) {
        setAgents((prev) => prev.filter((a) => a.id !== id))
      } else {
        const errorData = await response.json()
        throw new Error(errorData.error || "Failed to delete agent")
      }
    },
    [fetchWithAuth]
  )

  const refreshAgents = useCallback(loadAgents, [loadAgents])

  return {
    agents,
    isLoading,
    error,
    getAgent,
    createAgent,
    updateAgent,
    deleteAgent,
    refreshAgents,
  }
}
